import { create } from "zustand";
import type { SuggestedKeyword } from "../components/useAdsResearchStore";

// ── Types ────────────────────────────────────────────────────────────────────

export type GoogleCampaignType = "search" | "pmax";

export interface GoogleKeyword {
  text: string;
  matchType: "broad" | "phrase" | "exact";
}

export interface GoogleCampaignDeployResult {
  campaignId: string;
  customerId: string;
  name: string;
  type: string;
  status: string;
  adGroupId?: string;
  assetGroupId?: string;
}

// ── Store ────────────────────────────────────────────────────────────────────

interface GoogleCampaignLaunchState {
  isOpen: boolean;

  // Campaign config
  campaignType: GoogleCampaignType;
  campaignName: string;
  dailyBudget: string;
  currency: string;
  finalUrl: string;

  // Search-specific
  keywords: GoogleKeyword[];
  negativeKeywords: string[];
  headlines: string[];
  descriptions: string[];

  // PMax-specific
  imageAssetUrls: string[];

  // Launch state
  launchState: "idle" | "validating" | "publishing" | "done" | "error";
  launchError: string | null;
  deployResult: GoogleCampaignDeployResult | null;
  validationErrors: Record<string, string>;

  // Actions
  openModal: (opts?: {
    campaignType?: GoogleCampaignType;
    headlines?: string[];
    descriptions?: string[];
    keywords?: SuggestedKeyword[];
    negativeKeywords?: string[];
    imageAssetUrls?: string[];
  }) => void;
  closeModal: () => void;
  setCampaignType: (type: GoogleCampaignType) => void;
  setCampaignName: (name: string) => void;
  setDailyBudget: (budget: string) => void;
  setCurrency: (currency: string) => void;
  setFinalUrl: (url: string) => void;
  setKeywords: (keywords: GoogleKeyword[]) => void;
  addKeyword: (keyword: GoogleKeyword) => void;
  removeKeyword: (index: number) => void;
  setNegativeKeywords: (keywords: string[]) => void;
  addNegativeKeyword: (keyword: string) => void;
  removeNegativeKeyword: (index: number) => void;
  setHeadlines: (headlines: string[]) => void;
  updateHeadline: (index: number, value: string) => void;
  addHeadline: () => void;
  removeHeadline: (index: number) => void;
  setDescriptions: (descriptions: string[]) => void;
  updateDescription: (index: number, value: string) => void;
  addDescription: () => void;
  removeDescription: (index: number) => void;
  setImageAssetUrls: (urls: string[]) => void;
  setLaunchState: (state: GoogleCampaignLaunchState["launchState"]) => void;
  setLaunchError: (error: string | null) => void;
  setDeployResult: (result: GoogleCampaignDeployResult | null) => void;
  setValidationErrors: (errors: Record<string, string>) => void;
}

const INITIAL_STATE = {
  isOpen: false,
  campaignType: "search" as GoogleCampaignType,
  campaignName: "",
  dailyBudget: "",
  currency: "USD",
  finalUrl: "",
  keywords: [] as GoogleKeyword[],
  negativeKeywords: [] as string[],
  headlines: [] as string[],
  descriptions: [] as string[],
  imageAssetUrls: [] as string[],
  launchState: "idle" as const,
  launchError: null as string | null,
  deployResult: null as GoogleCampaignDeployResult | null,
  validationErrors: {} as Record<string, string>,
};

export const useGoogleCampaignLaunchStore = create<GoogleCampaignLaunchState>(
  (set) => ({
    ...INITIAL_STATE,

    openModal: (opts) =>
      set({
        isOpen: true,
        launchState: "idle",
        launchError: null,
        deployResult: null,
        validationErrors: {},
        campaignType: opts?.campaignType ?? "search",
        campaignName: "",
        dailyBudget: "",
        finalUrl: "",
        headlines: opts?.headlines ?? [],
        descriptions: opts?.descriptions ?? [],
        keywords: opts?.keywords?.map((kw) => ({ text: kw.text, matchType: kw.matchType })) ?? [],
        negativeKeywords: opts?.negativeKeywords ?? [],
        imageAssetUrls: opts?.imageAssetUrls ?? [],
      }),

    closeModal: () => set({ ...INITIAL_STATE }),

    setCampaignType: (campaignType) => set({ campaignType }),
    setCampaignName: (campaignName) => set({ campaignName }),
    setDailyBudget: (dailyBudget) => set({ dailyBudget }),
    setCurrency: (currency) => set({ currency }),
    setFinalUrl: (finalUrl) => set({ finalUrl }),

    setKeywords: (keywords) => set({ keywords }),
    addKeyword: (keyword) =>
      set((s) => ({ keywords: [...s.keywords, keyword] })),
    removeKeyword: (index) =>
      set((s) => ({ keywords: s.keywords.filter((_, i) => i !== index) })),

    setNegativeKeywords: (negativeKeywords) => set({ negativeKeywords }),
    addNegativeKeyword: (keyword) =>
      set((s) => ({ negativeKeywords: [...s.negativeKeywords, keyword] })),
    removeNegativeKeyword: (index) =>
      set((s) => ({
        negativeKeywords: s.negativeKeywords.filter((_, i) => i !== index),
      })),

    setHeadlines: (headlines) => set({ headlines }),
    updateHeadline: (index, value) =>
      set((s) => ({
        headlines: s.headlines.map((h, i) => (i === index ? value : h)),
      })),
    addHeadline: () =>
      set((s) => ({ headlines: [...s.headlines, ""] })),
    removeHeadline: (index) =>
      set((s) => ({ headlines: s.headlines.filter((_, i) => i !== index) })),

    setDescriptions: (descriptions) => set({ descriptions }),
    updateDescription: (index, value) =>
      set((s) => ({
        descriptions: s.descriptions.map((d, i) => (i === index ? value : d)),
      })),
    addDescription: () =>
      set((s) => ({ descriptions: [...s.descriptions, ""] })),
    removeDescription: (index) =>
      set((s) => ({
        descriptions: s.descriptions.filter((_, i) => i !== index),
      })),

    setImageAssetUrls: (imageAssetUrls) => set({ imageAssetUrls }),

    setLaunchState: (launchState) => set({ launchState }),
    setLaunchError: (launchError) => set({ launchError }),
    setDeployResult: (deployResult) => set({ deployResult }),
    setValidationErrors: (validationErrors) => set({ validationErrors }),
  }),
);
